import { all, call, put, select, takeLatest } from 'redux-saga/effects';
import { setActivityKeyword } from 'features/Search/SearchBox/SearchBox.saga';
import { defineMessages } from 'react-intl';
import { selectMeasurementSystem } from 'redux/modules/locale';
import { isLoggedIn } from 'redux/modules/user';
import { deepExtract } from 'helpers/deep';
import { distanceInMiles } from 'helpers/distance';
import { filterSetFilters } from 'redux/saga/selectors';
import { SEARCH } from 'constants/filter-sets';
import {
  searchFilterActions,
  selectSearchFilters,
  initializeMultiSelectFromHash,
  getDefaultVerticalId,
  selectSelectedVertical,
  getCreditsFilterValue,
  getVerticalOptions,
} from './filters.helpers';
import { setupFilters, selectActiveUrl } from '../Search.reducer';
import {
  selectFiltersFromSearchResults,
  selectLocationAutocompleteFromSearchResults,
  getUserLocationForSearch,
} from '../Search.helpers';
import { FILTER_NAMES, RESULT_TYPES, AUTO_RADIUS_ID, VERTICALS } from '../Search.constants';
import { selectSelectedKeywordId } from '../SearchBox/SearchBox.reducer';
import { SEARCH_PATH } from '../Search.route.path';
import { amenityMessages, levelMessages, radiusMessages } from './filters.messages';

const { addFilter } = searchFilterActions;

const { ACTIVITY, DATE, RADIUS, RESULT_TYPE, TIME, VERTICAL, AMENITY, LEVEL, CREDITS } = FILTER_NAMES;

const { SCHEDULE, VENUE } = RESULT_TYPES;

export const DEFAULT_RESULT_TYPE_LOGGED_IN = SCHEDULE;
export const DEFAULT_RESULT_TYPE_LOGGED_OUT = VENUE;

export const TIME_DEFAULT = '04:30-23:00';
export const TIME_OPTIONS = [
  {
    id: '05:00-08:00',
    ...defineMessages({
      name: { id: 'filters.time.early.name', defaultMessage: 'Early Morning' },
      description: { id: 'filters.time.early.description', defaultMessage: '5 am - 8 am' },
    }),
  },
  {
    id: '08:00-10:00',
    ...defineMessages({
      name: { id: 'filters.time.late-morning.name', defaultMessage: 'Late Morning' },
      description: { id: 'filters.time.late-morning.description', defaultMessage: '8 am - 10 am' },
    }),
  },
  {
    id: '10:00-14:00',
    ...defineMessages({
      name: { id: 'filters.time.midday.name', defaultMessage: 'Midday' },
      description: { id: 'filters.time.midday.description', defaultMessage: '10 am - 2 pm' },
    }),
  },
  {
    id: '14:00-17:00',
    ...defineMessages({
      name: { id: 'filters.time.afternoon.name', defaultMessage: 'Afternoon' },
      description: { id: 'filters.time.afternoon.description', defaultMessage: '2 pm - 5 pm' },
    }),
  },
  {
    id: '17:00-19:00',
    ...defineMessages({
      name: { id: 'filters.time.evening.name', defaultMessage: 'Evening' },
      description: { id: 'filters.time.evening.description', defaultMessage: '5 pm - 7 pm' },
    }),
  },
  {
    id: '19:00-23:00',
    ...defineMessages({
      name: { id: 'filters.time.late-evening.name', defaultMessage: 'Late evening' },
      description: { id: 'filters.time.late-evening.description', defaultMessage: 'After 7 pm' },
    }),
  },
];

export const ACTIVITY_OPTIONS_BY_VERTICAL = {
  [VERTICALS.fitness]: [
    {
      id: 'activity-1',
      ...defineMessages({
        name: { id: 'filters.activity.fitness.1.name', defaultMessage: 'Yoga' },
      }),
    },
    {
      id: 'activity-553',
      ...defineMessages({
        name: { id: 'filters.activity.fitness.553.name', defaultMessage: 'Barre' },
      }),
    },
    {
      id: '142',
      ...defineMessages({
        name: { id: 'filters.activity.fitness.142.name', defaultMessage: 'HIIT' },
      }),
    },
    {
      id: '140',
      ...defineMessages({
        name: { id: 'filters.activity.fitness.140.name', defaultMessage: 'Bootcamp' },
      }),
    },
    {
      id: 'activity-23',
      ...defineMessages({
        name: { id: 'filters.activity.fitness.23.name', defaultMessage: 'Martial arts' },
      }),
    },
    {
      id: 'activity-467',
      ...defineMessages({
        name: { id: 'filters.activity.fitness.467.name', defaultMessage: 'Rowing' },
      }),
    },
    {
      id: 'activity-589',
      ...defineMessages({
        name: { id: 'filters.activity.fitness.589.name', defaultMessage: 'Running' },
      }),
    },
    {
      id: 'activity-72',
      ...defineMessages({
        name: { id: 'filters.activity.fitness.72.name', defaultMessage: 'Cycling' },
      }),
    },
    {
      id: 'activity-90',
      ...defineMessages({
        name: { id: 'filters.activity.fitness.90.name', defaultMessage: 'Pilates' },
      }),
    },
    {
      id: 'activity-100',
      ...defineMessages({
        name: { id: 'filters.activity.fitness.100.name', defaultMessage: 'Dance' },
      }),
    },
    {
      id: 'activity-587',
      ...defineMessages({
        name: { id: 'filters.activity.fitness.587.name', defaultMessage: 'Boxing' },
      }),
    },
    {
      id: 'activity-591',
      ...defineMessages({
        name: { id: 'filters.activity.fitness.591.name', defaultMessage: 'Outdoors' },
      }),
    },
    {
      id: 'activity-590',
      ...defineMessages({
        name: { id: 'filters.activity.fitness.590.name', defaultMessage: 'Sports' },
      }),
    },
  ],
  [VERTICALS.wellness]: [
    {
      id: '1145',
      ...defineMessages({
        name: { id: 'filters.activity.wellness.1145.name', defaultMessage: 'Massage' },
      }),
    },
    {
      id: '1147',
      ...defineMessages({
        name: { id: 'filters.activity.wellness.1147.name', defaultMessage: 'Facial' },
      }),
    },
    {
      id: '1149',
      ...defineMessages({
        name: { id: 'filters.activity.wellness.1149.name', defaultMessage: 'Cryotherapy' },
      }),
    },
    {
      id: '1151',
      ...defineMessages({
        name: { id: 'filters.activity.wellness.1151.name', defaultMessage: 'Sports recovery' },
      }),
    },
    {
      id: '1153',
      ...defineMessages({
        name: { id: 'filters.activity.wellness.1153.name', defaultMessage: 'Sauna' },
      }),
    },
    {
      id: '1155',
      ...defineMessages({
        name: { id: 'filters.activity.wellness.1155.name', defaultMessage: 'Meditation' },
      }),
    },
    {
      id: '1157',
      ...defineMessages({
        name: { id: 'filters.activity.wellness.1157.name', defaultMessage: 'Acupuncture' },
      }),
    },
    {
      id: '1159',
      ...defineMessages({
        name: { id: 'filters.activity.wellness.1159.name', defaultMessage: 'Cupping' },
      }),
    },
    {
      id: '1161',
      ...defineMessages({
        name: { id: 'filters.activity.wellness.1161.name', defaultMessage: 'Beauty' },
      }),
    },
  ],
};

export const RADIUS_OPTIONS = {
  /* eslint-disable object-curly-newline */
  imperial: [
    { id: 1, defineMessage: radiusMessages.half_mile, radius: '1/2', value: 0.8 },
    { id: 2, defineMessage: radiusMessages.imperial, radius: '1', value: 1.6 },
    { id: 3, defineMessage: radiusMessages.imperial, radius: '5', value: 8 },
    { id: 4, defineMessage: radiusMessages.imperial, radius: '10', value: 16.1 },
    { id: 5, defineMessage: radiusMessages.imperial, radius: '25', value: 40.2 },
  ],
  metric: [
    { id: 6, defineMessage: radiusMessages.metric, radius: '1', value: 1 },
    { id: 7, defineMessage: radiusMessages.metric, radius: '2', value: 2 },
    { id: 8, defineMessage: radiusMessages.metric, radius: '5', value: 5 },
    { id: 9, defineMessage: radiusMessages.metric, radius: '10', value: 10 },
    { id: 10, defineMessage: radiusMessages.metric, radius: '20', value: 20 },
  ],
  /* eslint-enable object-curly-newline */
};

export const AMENITY_OPTIONS = [
  {
    id: 'shower',
    name: amenityMessages.showers,
  },
  {
    id: 'locker',
    name: amenityMessages.lockers,
  },
  {
    id: 'parking',
    name: amenityMessages.parking,
  },
];

export const LEVEL_OPTIONS = [
  {
    id: 'level_all',
    name: levelMessages.open,
  },
  {
    id: 'level_beginner',
    name: levelMessages.beginner,
  },
  {
    id: 'level_advanced',
    name: levelMessages.advanced,
  },
];

export const LOAD_VERTICALS = 'LOAD_VERTICALS';
export const LOAD_ACTIVITY = 'LOAD_ACTIVITY';
export const LOAD_AMENITY = 'LOAD_AMENITY';
export const LOAD_LEVEL = 'LOAD_LEVEL';
export const LOAD_DATE = 'LOAD_DATE';
export const LOAD_TIME = 'LOAD_TIME';
export const FILTER_CHANGED = 'classpass/search/FILTER_CHANGED';
export const FILTERS_CHANGED = 'classpass/search/FILTERS_CHANGED';
export const EXPAND_RADIUS = 'EXPAND_RADIUS';
export const LOAD_RADIUS = 'LOAD_RADIUS';
export const LOAD_CREDITS = 'LOAD_CREDITS';

export const loadVerticals = () => ({ type: LOAD_VERTICALS });
export const loadActivity = () => ({ type: LOAD_ACTIVITY });
export const loadAmenity = () => ({ type: LOAD_AMENITY });
export const loadLevel = () => ({ type: LOAD_LEVEL });
export const loadDate = () => ({ type: LOAD_DATE });
export const loadTime = () => ({ type: LOAD_TIME });
export const filterChanged = filter => ({ type: FILTER_CHANGED, filter });
export const filtersChanged = filters => ({ type: FILTERS_CHANGED, filters });
export const loadRadius = () => ({ type: LOAD_RADIUS });
export const loadCredits = () => ({ type: LOAD_CREDITS });

export const getActivityOptionsForVerticalId = verticalId =>
  verticalId === VERTICALS.all
    ? [].concat(...Object.values(ACTIVITY_OPTIONS_BY_VERTICAL))
    : ACTIVITY_OPTIONS_BY_VERTICAL[verticalId];

export function* doLoadVerticals() {
  const { vertical } = yield select(selectFiltersFromSearchResults);
  const defaultVerticalId = yield select(getDefaultVerticalId);

  const verticalOptions = getVerticalOptions(defaultVerticalId === VERTICALS.all);
  yield put(addFilter(VERTICAL, verticalOptions, undefined, { exclusive: true, value: vertical || defaultVerticalId }));
}

export function getRadiusByItemId(filters, itemId) {
  return deepExtract(`${RADIUS}.itemsById.${itemId}.value`, filters);
}

// Returns a radius only if the user selected one
export function getSelectedRadius(filters) {
  const selectedRadiusId = deepExtract(`${RADIUS}.value`, filters);
  return getRadiusByItemId(filters, selectedRadiusId);
}

export function* getLocalizedDisplayDistance(distance) {
  const measurementSystem = yield select(selectMeasurementSystem);
  const usesMetricSystem = measurementSystem === 'metric';
  const localizedDistance = usesMetricSystem ? distance : distanceInMiles(distance); // convert to mi

  return localizedDistance.toFixed(1);
}

export function* doLoadRadius() {
  const [measurementSystem, locationAutoCompleteFromHash, location] = yield all([
    select(selectMeasurementSystem),
    select(selectLocationAutocompleteFromSearchResults),
    select(getUserLocationForSearch),
  ]);

  const locationRadius = deepExtract('calculatedRadiusMiles', location);
  const locationRadiusKm = deepExtract('calculatedRadiusKm', location);
  const defaultDistance =
    locationRadius && locationRadiusKm
      ? {
          imperial: locationRadius,
          metric: locationRadiusKm,
        }
      : {
          imperial: deepExtract('calculated_radius_miles', locationAutoCompleteFromHash),
          metric: deepExtract('calculated_radius_km', locationAutoCompleteFromHash),
        };
  const radiusList = RADIUS_OPTIONS[measurementSystem];
  // Need to create a copy of our radius list to avoid mutating our base radius option lists
  const radiusListCopy = radiusList.slice(0);
  radiusListCopy.unshift({
    id: AUTO_RADIUS_ID,
    name: radiusMessages.auto,
    defineMessage: radiusMessages[measurementSystem],
    value: defaultDistance.metric,
    selectedDisplayValue: defaultDistance[measurementSystem],
  });

  const filterOptions = {
    valueFilter: true,
    value: AUTO_RADIUS_ID,
    unfilteredValue: AUTO_RADIUS_ID, // This indicates that the AUTO_RADIUS_ID represents an unfiltered state so the "filtered" state can be set correctly
  };
  yield put(addFilter(RADIUS, radiusListCopy, undefined, filterOptions));
}

export function* doAddActivityFilter(isSelectedByActivityName) {
  const selectedVertical = yield select(selectSelectedVertical);

  const activities = getActivityOptionsForVerticalId(selectedVertical);
  const filterOptions = {
    selected: isSelectedByActivityName,
  };
  yield put(addFilter(ACTIVITY, activities || [], undefined, filterOptions));
}

export function* doLoadActivity() {
  const currentKeywordSelectedId = yield select(selectSelectedKeywordId);
  const { tag: tags } = yield select(selectFiltersFromSearchResults);

  // If 1 tag, we initialize the searchbox with that tag.
  if (!currentKeywordSelectedId && tags && tags.length === 1) {
    yield call(setActivityKeyword, tags[0], {
      excludeFromWriteActions: true,
    });
  }

  let isSelectedByActivityName = {};
  // If 2 or more tags, we initialize the activity filter with those tags.
  if (tags && tags.length >= 2) {
    isSelectedByActivityName = tags.reduce((acc, activity) => ({ ...acc, [activity]: true }), {});
  }

  yield call(doAddActivityFilter, isSelectedByActivityName);
}

export function* doLoadAmenity() {
  const filtersFromHash = yield select(selectFiltersFromSearchResults);
  const amenityKeys = ['shower', 'locker', 'parking'];

  const isSelectedByAmenityName = initializeMultiSelectFromHash(amenityKeys, filtersFromHash);

  const filterOptions = {
    selected: isSelectedByAmenityName,
  };
  yield put(addFilter(AMENITY, AMENITY_OPTIONS, undefined, filterOptions));
}

export function* doLoadLevel() {
  const filtersFromHash = yield select(selectFiltersFromSearchResults);
  const levelKeys = ['level_all', 'level_beginner', 'level_advanced'];

  const isSelectedByLevelName = initializeMultiSelectFromHash(levelKeys, filtersFromHash);

  const filterOptions = {
    selected: isSelectedByLevelName,
  };
  yield put(addFilter(LEVEL, LEVEL_OPTIONS, undefined, filterOptions));
}

export function* doLoadCredits() {
  const filtersFromHash = yield select(selectFiltersFromSearchResults);
  const { min_credits: minCredits, max_credits: maxCredits } = filtersFromHash;

  const creditsRangeValue = getCreditsFilterValue(minCredits, maxCredits);

  const filterOptions = {
    value: creditsRangeValue,
    valueFilter: true,
  };
  yield put(addFilter(CREDITS, [], undefined, filterOptions));
}

export function* doLoadDate() {
  const { date: dateFilter } = yield select(selectSearchFilters);
  if (dateFilter && dateFilter.value) {
    return;
  }

  const { date } = yield select(selectFiltersFromSearchResults);
  yield put(addFilter(DATE, [], undefined, { valueFilter: true, value: date }));
}

export function* doLoadTime() {
  const { time } = yield select(selectSearchFilters);
  if (time && time.value) {
    return;
  }

  const { ft, tt } = yield select(selectFiltersFromSearchResults);
  yield put(addFilter(TIME, TIME_OPTIONS, undefined, { valueFilter: true, value: (ft || tt) && `${ft}-${tt}` }));
}

export function* getDefaultResultType() {
  const isUserLoggedIn = yield select(isLoggedIn);
  const activeUrl = yield select(selectActiveUrl);
  // If user url has no hashes we should return the appropriate defaults
  if (activeUrl === SEARCH_PATH) {
    return isUserLoggedIn ? DEFAULT_RESULT_TYPE_LOGGED_IN : DEFAULT_RESULT_TYPE_LOGGED_OUT;
  }
  // Infer the result type from the search results, or use appropriate default
  if (isUserLoggedIn) {
    const { result_type: resultTypeFromHash } = yield select(selectFiltersFromSearchResults);
    return resultTypeFromHash || DEFAULT_RESULT_TYPE_LOGGED_IN;
  }
  return DEFAULT_RESULT_TYPE_LOGGED_OUT;
}

export function* getResultType() {
  const searchFilters = yield select(filterSetFilters, SEARCH);
  const selectedResultType = deepExtract(`${RESULT_TYPE}.value`, searchFilters);
  // If the user selected a result type explicitly, use that
  if (selectedResultType) {
    return selectedResultType;
  }
  const defaultResultType = yield call(getDefaultResultType);

  return defaultResultType;
}

export function* doLoadResultType() {
  const resultType = yield call(getDefaultResultType);
  yield put(addFilter(RESULT_TYPE, [], undefined, { exclusive: true, value: resultType }));
}

export function* doSetupFilters() {
  yield call(doLoadVerticals);
  yield all([
    call(doLoadResultType),
    call(doLoadActivity),
    call(doLoadAmenity),
    call(doLoadLevel),
    call(doLoadDate),
    call(doLoadTime),
    call(doLoadRadius),
    call(doLoadCredits),
  ]);
}

export function* watchFilters() {
  yield takeLatest(LOAD_VERTICALS, doLoadVerticals);
  yield takeLatest(LOAD_ACTIVITY, doLoadActivity);
  yield takeLatest(LOAD_AMENITY, doLoadAmenity);
  yield takeLatest(LOAD_LEVEL, doLoadLevel);
  yield takeLatest(LOAD_DATE, doLoadDate);
  yield takeLatest(LOAD_TIME, doLoadTime);
  yield takeLatest(setupFilters().type, doSetupFilters);
  yield takeLatest(LOAD_RADIUS, doLoadRadius);
  yield takeLatest(LOAD_CREDITS, doLoadCredits);
}
